/**
 * IronCase API client.
 *
 * Targets the Lambda function URL provisioned by `infra/modules/app-runtime`
 * via ADR-0016. Base URL is configurable per environment via the Vite
 * `VITE_API_BASE_URL` env var; defaults to the live dev Lambda URL.
 *
 * No auth headers yet — better-auth migration to Postgres is tracked in #115.
 * Once that lands, every request gets a session cookie / Bearer token here.
 */

import { z } from "zod";

import { container } from "../hex/container.js";

/**
 * Token getter injected by main.tsx at boot — kept as a function so the
 * Cognito session can be refreshed transparently between calls.
 *
 * Forwards to the container so the adapter and this client share one source of
 * truth for the bearer token.
 */
export function setTokenGetter(fn: () => string | null) {
  container().setTokenProvider(fn);
}

// -- Schemas ----------------------------------------------------------------

export const HealthSchema = z.object({
  status: z.string(),
  timestamp: z.string(),
  runtime: z.string().optional(),
  region: z.string().optional(),
  functionName: z.string().optional(),
});
export type Health = z.infer<typeof HealthSchema>;

// Mirrors the backend BCM Casework enum exactly (apps/api/src/contexts/
// casework/domain/case-record.ts CASE_STATUSES). Legacy "in_review" was
// migrated to "suspended" server-side; keeping these in lock-step is what
// lets the whole cases list parse — a drift here Zod-fails every case row.
export const CaseStatus = z.enum([
  "open",
  "assigned",
  "active",
  "suspended",
  "closed",
  "archived",
]);
export type CaseStatus = z.infer<typeof CaseStatus>;

export const CaseSchema = z.object({
  id: z.string(),
  caseNumber: z.string(),
  title: z.string(),
  type: z.string(),
  status: CaseStatus,
  agency: z.string(),
  leadDetective: z.string().optional(),
  evidenceCount: z.number().int().nonnegative().default(0),
  personCount: z.number().int().nonnegative().default(0),
  gapCount: z.number().int().nonnegative().default(0),
  flagged: z.boolean().default(false),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Case = z.infer<typeof CaseSchema>;

// -- CaseOverview shared provenance / tri-state enums ----------------------
// CAP 2 (entity extraction → source reference) + the warrant compliance
// columns (CAP 3 Search Warrant Overview) lean on these across every tab, so
// they live above the schemas that consume them.

/**
 * A back-reference from an extracted field/entity to the source document +
 * location it came from (CAP 2). Every auto-populated value can carry one so
 * the UI and the export can cite "where did this come from".
 */
export const SourceRefSchema = z.object({
  /** Evidence id the value was extracted from (e.g. "ev-004"). */
  evidenceId: z.string(),
  /** Human label, e.g. "Witness interview p.3". */
  label: z.string(),
  /** Fine-grained locator within the source ("p.3", "row 142", "00:14:22"). */
  locator: z.string().optional(),
});
export type SourceRef = z.infer<typeof SourceRefSchema>;

/** Tri-state Yes / No / N-A used across the warrant compliance columns. */
export const YesNoNa = z.enum(["yes", "no", "na"]);
export type YesNoNa = z.infer<typeof YesNoNa>;

/** Yes / No / Partial (warrant "All Data Received"). */
export const YesNoPartial = z.enum(["yes", "no", "partial"]);
export type YesNoPartial = z.infer<typeof YesNoPartial>;

// Evidence ------------------------------------------------------------------
export const EvidenceKind = z.enum([
  "document",
  "image",
  "video",
  "audio",
  "scene_note",
  "interview_transcript",
  "device_extract",
]);
export type EvidenceKind = z.infer<typeof EvidenceKind>;

export const HitlStatus = z.enum(["not_required", "pending", "approved", "rejected"]);
export type HitlStatus = z.infer<typeof HitlStatus>;

export const EvidenceItemSchema = z.object({
  id: z.string(),
  caseId: z.string(),
  kind: EvidenceKind,
  title: z.string(),
  source: z.string(),
  uploadedAt: z.string(),
  uploadedBy: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  entityCount: z.number().int().nonnegative().default(0),
  hitl: HitlStatus.default("not_required"),
  cocSealed: z.boolean().default(false),
  // -- CAP 3 Evidence Items tab columns (all optional/defaulted) -----------
  /** "Item #" — booked evidence item number; BLANK => unbooked (gap B-a). */
  itemNumber: z.string().optional(),
  /** Report # the item is associated with. */
  reportNumber: z.string().optional(),
  /** Physical/booking location. */
  location: z.string().optional(),
  /** "Forensic Analysis Needed". */
  forensicAnalysisNeeded: z.boolean().default(false),
  /** "Type of Analysis Needed" — "Latent print (AFIS)", "DNA", "Ballistics"… */
  analysisType: z.string().optional(),
  /** "Warrant Needed". */
  warrantRequired: z.boolean().default(false),
  /** "Sent to Lab" — ISO date; ABSENT while forensicAnalysisNeeded => gap C-d. */
  sentToLab: z.string().optional(),
  /** "Lab Used". */
  labUsed: z.string().optional(),
  /** "Analyzed" — ISO date. */
  analyzedAt: z.string().optional(),
  /** "Returned" — ISO date. */
  returnedAt: z.string().optional(),
  /** Free-text notes. */
  notes: z.string().optional(),
  /** CAP 2 provenance — where each extracted value came from. */
  sourceRefs: z.array(SourceRefSchema).default([]),
  /** Field names IronCase auto-populated (drives visual-distinct + export fill). */
  autoFields: z.array(z.string()).default([]),
});
export type EvidenceItem = z.infer<typeof EvidenceItemSchema>;

// Persons -------------------------------------------------------------------
/**
 * Roles as the API emits them — camelCase.
 *
 * This enum said "person_of_interest" while the API sends "personOfInterest".
 * Three layers disagreed: the database column stores snake_case, the domain
 * union is camelCase, and this schema followed the database rather than the
 * contract it actually parses. Only the multi-word role diverged, so five of
 * six matched by accident and the mismatch stayed hidden until a case with a
 * person-of-interest was opened — at which point the whole persons list failed
 * to parse and the tab read "Persons 0" for a case with six.
 *
 * The API response is the contract. Conversion belongs at the persistence
 * boundary (see persons-repository.ts), not here.
 */
export const PersonRole = z.enum([
  "suspect",
  "victim",
  "witness",
  "officer",
  "personOfInterest",
  "associate",
]);
export type PersonRole = z.infer<typeof PersonRole>;

export const PersonSchema = z.object({
  id: z.string(),
  caseId: z.string(),
  fullName: z.string(),
  role: PersonRole,
  ageBracket: z.string().optional(),
  badge: z.string().optional(),
  agency: z.string().optional(),
  linkedEvidence: z.number().int().nonnegative().default(0),
  verified: z.boolean().default(false),
  verifiedBy: z.string().optional(),
  confidence: z.number().min(0).max(1).optional(),
  lastSeen: z.string().optional(),
  // -- CAP 3 Persons Index tab columns (all optional/defaulted) ------------
  /** "Reports Mentioned In" — report #s, e.g. ["DR 26-00945-S1"]. */
  reportsMentionedIn: z.array(z.string()).default([]),
  /** "Interviewed Y/N". */
  interviewed: z.boolean().default(false),
  /** "Interview Date" — ISO date. */
  interviewDate: z.string().optional(),
  /** ev-### of the interview/statement doc, if one exists (drives gap A-a). */
  interviewEvidenceId: z.string().optional(),
  /** Free-text notes. */
  notes: z.string().optional(),
  /** CAP 2 provenance — where this person was extracted from. */
  sourceRefs: z.array(SourceRefSchema).default([]),
  /** Field names IronCase auto-populated. */
  autoFields: z.array(z.string()).default([]),
});
export type Person = z.infer<typeof PersonSchema>;

// Warrants ------------------------------------------------------------------
export const WarrantStatus = z.enum([
  "draft",
  "pending_hitl",
  "rejected",
  "signed",
  "served",
  "returned",
  "executed",
  "expired",
]);
export type WarrantStatus = z.infer<typeof WarrantStatus>;

export const WarrantType = z.enum([
  // -- Full domain warrant-type list (CAP 5 selector + Search Warrant board) --
  "cdr",
  "social_media",
  "surveillance_footage",
  "tower_dump",
  "area_search",
  "phone_ping",
  "vehicle_tracker",
  "residential_commercial",
  "ramey",
  "mincey",
  "apple_data",
  "google_data",
  "google_geofence",
  "phone_dump",
  "business_documents",
  // -- Legacy values kept so existing mock warrants still parse ---------------
  "search",
  "arrest",
  "telecom_records",
  "device_search",
  "calecpa",
  "other",
]);
export type WarrantType = z.infer<typeof WarrantType>;

/** Display labels for every warrant type (selector + board "Warrant Type" col). */
export const WARRANT_TYPE_LABELS: Record<WarrantType, string> = {
  cdr: "CDRs",
  social_media: "Social Media",
  surveillance_footage: "Surveillance Footage",
  tower_dump: "Tower Dump",
  area_search: "Area Search",
  phone_ping: "Phone Ping",
  vehicle_tracker: "Vehicle Tracker",
  residential_commercial: "Residential / Commercial",
  ramey: "Ramey",
  mincey: "Mincey",
  apple_data: "Apple Data",
  google_data: "Google Data",
  google_geofence: "Google Geofence",
  phone_dump: "Phone Dump",
  business_documents: "Business Documents",
  search: "Search",
  arrest: "Arrest",
  telecom_records: "Telecom Records",
  device_search: "Device Search",
  calecpa: "CalECPA",
  other: "Other",
};

/** Service providers a warrant can be served on (Search Warrant board). */
export const ServiceProvider = z.enum([
  "t_mobile",
  "att",
  "verizon",
  "meta",
  "apple",
  "google",
  "hertz",
  "other",
]);
export type ServiceProvider = z.infer<typeof ServiceProvider>;

/** Display labels for service providers. */
export const SERVICE_PROVIDER_LABELS: Record<ServiceProvider, string> = {
  t_mobile: "T-Mobile",
  att: "AT&T",
  verizon: "Verizon",
  meta: "Meta",
  apple: "Apple",
  google: "Google",
  hertz: "Hertz",
  other: "Other",
};

/** "Method of Notification" for CalECPA notifications (Search Warrant board). */
export const NotificationMethod = z.enum([
  "mail",
  "email",
  "text",
  "clew_website",
  "in_person",
]);
export type NotificationMethod = z.infer<typeof NotificationMethod>;

/** Display labels for the notification methods. */
export const NOTIFICATION_METHOD_LABELS: Record<NotificationMethod, string> = {
  mail: "Mail",
  email: "Email",
  text: "Text",
  clew_website: "CLEW Website",
  in_person: "In Person",
};

/**
 * Color-coded DISPLAY status for the Search Warrant Overview board (CAP 3).
 * Distinct from `Warrant.status` (the lifecycle state); the SW-Overview
 * component maps/derives this for the first column.
 */
export const WarrantBoardStatus = z.enum([
  "completed",
  "no_action_required",
  "action_required",
  "action_required_urgent",
]);
export type WarrantBoardStatus = z.infer<typeof WarrantBoardStatus>;

/** Display labels for the board status (color-coded Tag). */
export const WARRANT_BOARD_STATUS_LABELS: Record<WarrantBoardStatus, string> = {
  completed: "Completed",
  no_action_required: "No Action Required",
  action_required: "Action Required",
  action_required_urgent: "Action Required URGENT",
};

export const WarrantSchema = z.object({
  id: z.string(),
  caseId: z.string(),
  warrantNumber: z.string(),
  type: WarrantType,
  status: WarrantStatus,
  target: z.string(),
  draftedAt: z.string(),
  draftedBy: z.string(),
  signedBy: z.string().optional(),
  court: z.string().optional(),
  servedAt: z.string().optional(),
  returnFiledAt: z.string().optional(),
  calecpaPredicate: z.string().optional(),
  expiresAt: z.string().optional(),
  /** The probable-cause narrative a judge reads. */
  affidavit: z.string().optional(),
  /**
   * Present = a model drafted the probable-cause argument. Surfaced to the UI
   * on purpose: a supervisor must see this before signing, and keeping it
   * database-only would make the disclosure invisible at the point of decision.
   */
  affidavitModelId: z.string().optional(),
  affidavitDraftedAt: z.string().optional(),
  submittedBy: z.string().optional(),
  submittedAt: z.string().optional(),
  reviewedBy: z.string().optional(),
  reviewedAt: z.string().optional(),
  reviewReason: z.string().optional(),
  // -- CAP 3 Search Warrant Overview ~24-col set (all optional/defaulted) ---
  /** "Author" (distinct from draftedBy provenance). */
  author: z.string().optional(),
  /** "Date Signed" — ISO date (distinct from draftedAt). */
  dateSignedAt: z.string().optional(),
  /** "Courthouse". */
  courthouse: z.string().optional(),
  /** "Judge". */
  judge: z.string().optional(),
  /** "Warrant Object" — what the warrant authorizes seizing/obtaining. */
  warrantObject: z.string().optional(),
  /** "Misc. Notes". */
  miscNotes: z.string().optional(),
  /** "Service Provider". */
  serviceProvider: ServiceProvider.optional(),
  /** "Service Provider Tracking ID / Case #". */
  serviceProviderTrackingId: z.string().optional(),
  /** "DON Start Date" — CalECPA Delayed-Online-Notice start (ISO date). */
  donStartDate: z.string().optional(),
  /** "DON Expiration Date" — feeds Category-C flag (flag 72h before). ISO. */
  donExpirationDate: z.string().optional(),
  /** "Data Expiration Date" — ISO date. */
  dataExpirationDate: z.string().optional(),
  /** "CalECPA Notifications" (Yes/No/N-A). */
  calecpaNotifications: YesNoNa.optional(),
  /** "Method of Notification". */
  notificationMethod: NotificationMethod.optional(),
  /** "All Data Received" (Yes/No/Partial). */
  allDataReceived: YesNoPartial.optional(),
  /** "Data Filed" (Yes/No). */
  dataFiled: z.boolean().default(false),
  /** "SW Return" (Yes/No). */
  swReturn: z.boolean().default(false),
  /** "SW Copy Attached to Case File" (Yes/No). */
  swCopyAttached: z.boolean().default(false),
  /** "WCPD Report Number". */
  wcpdReportNumber: z.string().optional(),
  /** "Search Warrant Number" — human display # (distinct from warrantNumber id). */
  searchWarrantNumber: z.string().optional(),
  /** "Discovery Provided to DA" (Yes/No). */
  discoveryProvidedToDa: z.boolean().default(false),
  /** E.164 pertinent phone this warrant covers — join key for gap C-c. */
  pertinentPhone: z.string().optional(),
  /** Color-coded board status; when absent the SW-Overview derives it. */
  boardStatus: WarrantBoardStatus.optional(),
  /** CAP 2 provenance — where extracted warrant fields came from. */
  sourceRefs: z.array(SourceRefSchema).default([]),
  /** Field names IronCase auto-populated. */
  autoFields: z.array(z.string()).default([]),
});
export type Warrant = z.infer<typeof WarrantSchema>;

// Surveillance --------------------------------------------------------------
export const SurveillanceClipSchema = z.object({
  id: z.string(),
  caseId: z.string(),
  title: z.string(),
  source: z.string(),
  capturedAt: z.string(),
  durationSeconds: z.number().int().nonnegative(),
  transcriptStatus: z.enum(["pending", "transcribing", "available", "failed"]),
  transcriptConfidence: z.number().min(0).max(1).optional(),
  preservationConfirmed: z.boolean().default(false),
  // -- CAP 3 Surveillance Video tab columns (all optional/defaulted) -------
  /** "Location Name" — identified camera location (set => an identified source). */
  locationName: z.string().optional(),
  /** "Address". */
  address: z.string().optional(),
  /** "Collected By". */
  collectedBy: z.string().optional(),
  /** "Date Collected" — ISO; ABSENT + camera identified => gap A-b / C-b. */
  dateCollected: z.string().optional(),
  /** "Date/Time Period Covered" — e.g. "2026-02-13 00:00–04:00". */
  periodCovered: z.string().optional(),
  /** "Booked". */
  booked: z.boolean().default(false),
  /** "Report #" — collection report number; ABSENT + identified => gap A-b. */
  reportNumber: z.string().optional(),
  /** "Reviewed". */
  reviewed: z.boolean().default(false),
  /** Download confirmed; FALSE while collected/identified => gap C-b. */
  downloadConfirmed: z.boolean().default(false),
  /** CAP 2 provenance — where this source was identified. */
  sourceRefs: z.array(SourceRefSchema).default([]),
  /** Field names IronCase auto-populated. */
  autoFields: z.array(z.string()).default([]),
});
export type SurveillanceClip = z.infer<typeof SurveillanceClipSchema>;

/**
 * GET /api/cases returns a PAGE: `{ items, nextCursor, total }`.
 *
 * This schema said `cases` and carried `.default([])`, so when the key was
 * absent zod supplied an empty array and the cases index rendered "0 of 0
 * cases" against a response that contained five of them. No error was raised
 * anywhere — the mismatch failed open into "there is no data", which is
 * indistinguishable from an empty agency and is the worst possible way for a
 * contract break to present.
 *
 * `items` is therefore REQUIRED here. A missing key is now a parse error the
 * caller surfaces, because being told the shape changed is strictly better than
 * being shown an empty list.
 */
const CasesResponseSchema = z.object({
  items: z.array(CaseSchema),
  nextCursor: z.string().nullable().optional(),
  total: z.number().nullable().optional(),
});

const EvidenceResponseSchema = z.object({
  evidence: z.array(EvidenceItemSchema).default([]),
});
const PersonsResponseSchema = z.object({
  persons: z.array(PersonSchema).default([]),
});
const WarrantsResponseSchema = z.object({
  warrants: z.array(WarrantSchema).default([]),
});
const SurveillanceResponseSchema = z.object({
  surveillance: z.array(SurveillanceClipSchema).default([]),
});

// Per-user favorites — generic across resource types ("case", "app", ...).
// Server-side is the source of truth; localStorage is an optimistic cache.
export const FavoriteSchema = z.object({
  resourceType: z.string(),
  resourceId: z.string(),
});
const FavoritesResponseSchema = z.object({ favorites: z.array(FavoriteSchema).default([]) });
const FavoriteOkSchema = z.object({ status: z.string() });
export type Favorite = z.infer<typeof FavoriteSchema>;

export const MeSchema = z.object({
  id: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  agencyId: z.string().nullable(),
  role: z.string().nullable(),
  sessionExpiresAt: z.string(),
});
export type Me = z.infer<typeof MeSchema>;

// Multipass — Identity & Access settings surfaces --------------------------
// Schemas + types for /settings/{roles, organizations, markings,
// authorized-applications}. No backend endpoints exist yet; the hooks in
// lib/hooks.ts return mock data and call out the TODO. Once the IAM API
// lands these will be wired through the standard request() helper.

export const RoleSetSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Foundry-style context label (e.g. "Marketplace installation", "Ontology"). */
  context: z.string(),
  /** Agency id that owns this role set, when scoped to one agency. */
  organizationId: z.string().nullable().default(null),
});
export type RoleSet = z.infer<typeof RoleSetSchema>;

export const OrganizationSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().default(""),
});
export type Organization = z.infer<typeof OrganizationSchema>;

export const MarkingSchema = z.object({
  id: z.string(),
  name: z.string(),
  category: z.string(),
  /** ISO-8601 timestamp. */
  createdAt: z.string(),
});
export type Marking = z.infer<typeof MarkingSchema>;

export const AuthorizedApplicationSchema = z.object({
  id: z.string(),
  /** OAuth client name surfaced to the user. */
  name: z.string(),
  /** Agency that manages the OAuth client. */
  managingAgencyId: z.string().nullable().default(null),
  /** Optional URL/data-URI for the app logo. */
  imageUrl: z.string().optional(),
});
export type AuthorizedApplication = z.infer<typeof AuthorizedApplicationSchema>;

export const RoleSetsResponseSchema = z.object({ roleSets: z.array(RoleSetSchema).default([]) });
export const OrganizationsResponseSchema = z.object({
  organizations: z.array(OrganizationSchema).default([]),
});
export const MarkingsResponseSchema = z.object({ markings: z.array(MarkingSchema).default([]) });
export const AuthorizedApplicationsResponseSchema = z.object({
  applications: z.array(AuthorizedApplicationSchema).default([]),
});

// -- RBAC admin — Roles & Cedar policies (ADR-0022 Phase 3) -----------------
// Typed against the JSON contracts in
// .planning/phases/12-admin-settings-ui/12-01-SUMMARY.md (ADMIN-01/02).
// The SPA only ever talks to /api/admin/rbac/* — never to AVP directly. Every
// route is platform-admin gated; agencyId is derived from the session, never
// sent by the client. Reads + every mutation are audit-logged server-side
// (fail-closed) per 12-01, so no client-side audit is needed.

export const RoleSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().default(""),
  permissions: z.array(z.string()).default([]),
});
export type Role = z.infer<typeof RoleSchema>;

/** Request body for POST/PUT /api/admin/rbac/roles[/:id]. */
export interface RoleInput {
  name: string;
  description: string;
  permissions: string[];
}

export const RolePolicySchema = z.object({
  policyId: z.string(),
  cedarText: z.string(),
});
export type RolePolicy = z.infer<typeof RolePolicySchema>;

const RolesResponseSchema = z.object({ roles: z.array(RoleSchema).default([]) });
const RoleResponseSchema = z.object({ role: RoleSchema });
const RolePolicyResponseSchema = z.object({ policy: RolePolicySchema });
const RoleDeletedResponseSchema = z.object({
  status: z.string(),
  roleId: z.string(),
});
export type RoleDeletedResponse = z.infer<typeof RoleDeletedResponseSchema>;

// -- RBAC admin — Principal assignments (ADR-0022 Phase 3, ADMIN-03/05/06) --
// A Principal is a user or a group granted a role. Typed against the
// "Principal assignments" contract in 12-01-SUMMARY.md:
//
//   GET    /api/admin/rbac/roles/:roleId/principals
//            → { principals: [{ principalType, principalId, displayName }] }
//   POST   /api/admin/rbac/roles/:roleId/principals
//            body { principalType, principalId } → 201 { status, principalType, principalId }
//   DELETE /api/admin/rbac/roles/:roleId/principals/:principalId?type=user|group
//            → 200 { status, principalType, principalId }
//
// Foundry vocabulary: a *Principal* is a User or a Group.

export const PrincipalType = z.enum(["user", "group"]);
export type PrincipalType = z.infer<typeof PrincipalType>;


/** One row of the hash-chained audit trail. */
export const AuditEntrySchema = z.object({
  id: z.string(),
  actorEmail: z.string().default(""),
  action: z.string().default(""),
  method: z.string().default(""),
  path: z.string().default(""),
  resourceType: z.string().nullable().default(null),
  resourceId: z.string().nullable().default(null),
  outcome: z.string().default(""),
  statusCode: z.number().default(0),
  sensitive: z.boolean().default(false),
  createdAt: z.string().default(""),
});
export type AuditEntry = z.infer<typeof AuditEntrySchema>;
const AuditResponseSchema = z.object({
  items: z.array(AuditEntrySchema).default([]),
  nextCursor: z.string().nullable().default(null),
});


/** Body for POST /api/admin/identity-providers — mirrors parseCreateProviderBody. */
export interface IdentityProviderInput {
  name: string;
  type: "SAML" | "OIDC";
  attributeMapping: Record<string, string>;
  saml?: { metadataUrl?: string; metadataXml?: string; enableIdpSignOut?: boolean };
  oidc?: { clientId: string; clientSecret: string; issuer: string };
}
const IdentityProviderWriteSchema = z.object({
  status: z.string().default(""),
  name: z.string().default(""),
});

export const IdentityProviderSchema = z.object({
  name: z.string(),
  type: z.string().default(""),
  enabled: z.boolean().default(true),
});
export type IdentityProvider = z.infer<typeof IdentityProviderSchema>;
const IdentityProvidersResponseSchema = z.object({
  providers: z.array(IdentityProviderSchema).default([]),
  error: z.string().optional(),
});

export const PrincipalAssignmentSchema = z.object({
  principalType: PrincipalType,
  principalId: z.string(),
  /** Friendly label; the V1 backend echoes the id when it has no directory. */
  displayName: z.string().default(""),
  /** Email (directory users) — shown as the picker subtitle. */
  email: z.string().optional(),
  /** Informal nickname (directory users), e.g. "Jon". */
  nickname: z.string().optional(),
});
export type PrincipalAssignment = z.infer<typeof PrincipalAssignmentSchema>;

/** Request body for POST /api/admin/rbac/roles/:roleId/principals. */
export interface PrincipalRef {
  principalType: PrincipalType;
  principalId: string;
}

const PrincipalsResponseSchema = z.object({
  principals: z.array(PrincipalAssignmentSchema).default([]),
});

const PrincipalAssignedResponseSchema = z.object({
  status: z.string(),
  principalType: PrincipalType,
  principalId: z.string(),
});
export type PrincipalAssignedResponse = z.infer<typeof PrincipalAssignedResponseSchema>;

// -- P6: Human-in-the-loop approvals (HumanDecisioning context) -------------
// Wire shape mirrors apps/api approvalDto (mappers.ts) EXACTLY. "AI assists,
// humans decide." — a sensitive AI-assisted action (warrant draft, arrest rec)
// is parked as a `pending` Approval and a DIFFERENT human reviewer decides it.
// agency_id is NOT on the wire (RLS walls it); requestedBy IS, so the SPA can
// render the author and grey out the decide for them (the author≠approver SoD
// the API enforces with a 403). Backed by the real /api/v1/approvals endpoints.

export const ApprovalRiskLevel = z.enum(["medium", "high", "critical"]);
export type ApprovalRiskLevel = z.infer<typeof ApprovalRiskLevel>;

export const ApprovalStatus = z.enum(["pending", "approved", "rejected"]);
export type ApprovalStatus = z.infer<typeof ApprovalStatus>;

/** The two decisions a reviewer may render (POST .../decide body). */
export const ApprovalDecision = z.enum(["approve", "reject"]);
export type ApprovalDecision = z.infer<typeof ApprovalDecision>;

export const ApprovalSchema = z.object({
  id: z.string(),
  riskLevel: ApprovalRiskLevel,
  status: ApprovalStatus,
  actionType: z.string(),
  resourceType: z.string(),
  resourceId: z.string(),
  title: z.string(),
  summary: z.string(),
  /** The AUTHOR's Cognito `sub` — compare to `session.sub` for the SoD UI. */
  requestedBy: z.string(),
  /** AI-assist provenance: how the machine arrived at the suggestion. */
  ai: z.object({
    confidence: z.number().min(0).max(1).optional(),
    reasoning: z.string().optional(),
    model: z.string().optional(),
    evidenceInputIds: z.array(z.string()).default([]),
  }),
  /** Mandatory once decided — the reviewer's rationale (CJIS AC-5 record). */
  reviewerNotes: z.string().optional(),
  decidedBy: z.string().optional(),
  decidedAt: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Approval = z.infer<typeof ApprovalSchema>;

const ApprovalsResponseSchema = z.object({ items: z.array(ApprovalSchema).default([]) });
const ApprovalResponseSchema = z.object({ approval: ApprovalSchema });

/** Optional server-side filters for the queue (mirrors ?status=&riskLevel=). */
export interface ApprovalListFilter {
  status?: ApprovalStatus;
  riskLevel?: ApprovalRiskLevel;
}

// -- Fetch wrapper ----------------------------------------------------------

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  schema: z.ZodSchema<T>,
  init?: RequestInit,
): Promise<T> {
  // Origin resolution and bearer attachment live in the HttpClientPort adapter
  // (hex/adapters/fetch-http.ts). This function keeps what is genuinely the API
  // client's job: routes, status handling, and schema validation.
  const res = await container().http.send({
    path,
    method: (init?.method as "GET" | "POST" | "PUT" | "PATCH" | "DELETE") ?? "GET",
    // `init.body` is already a serialized string here; the port would
    // re-serialize an object, so pass it through as an explicit header/body pair.
    headers: {
      Accept: "application/json",
      ...(init?.body === undefined ? {} : { "Content-Type": "application/json" }),
      ...((init?.headers as Record<string, string> | undefined) ?? {}),
    },
    ...(init?.body === undefined ? {} : { body: JSON.parse(init.body as string) as unknown }),
    ...(init?.signal ? { signal: init.signal } : {}),
  });

  if (!res.ok) {
    // Keep the server's own explanation.
    //
    // This used to throw "403 Forbidden — /api/cases" and drop the response
    // body, so the one useful sentence the API had already written —
    // "authenticated user has no agency_id; cannot scope request" — never
    // reached the screen. The UI then filled the silence with a guess about
    // Lambda cold starts and offered a Retry that could not possibly work.
    let serverMessage = "";
    try {
      const body = (await res.clone().json()) as { error?: unknown };
      if (typeof body?.error === "string") serverMessage = body.error;
    } catch {
      // Not JSON, or already consumed — fall back to the status line.
    }
    throw new ApiError(
      res.status,
      serverMessage || `${res.status} ${res.statusText} — ${path}`
    );
  }

  const json = (await res.json()) as unknown;
  return schema.parse(json);
}

// -- Capability 1 + 2: Upload + Extract ------------------------------------

export const UploadUrlResponseSchema = z.object({
  evidence: z.object({
    id: z.string(),
    caseId: z.string(),
    kind: z.string(),
    title: z.string(),
    source: z.string(),
    uploadedAt: z.string(),
    uploadedBy: z.string(),
    sizeBytes: z.number(),
  }).passthrough(),
  uploadUrl: z.string().url(),
  key: z.string(),
  bucket: z.string(),
});
export type UploadUrlResponse = z.infer<typeof UploadUrlResponseSchema>;

export const ExtractedPageSchema = z.object({
  pageNumber: z.number().int(),
  text: z.string(),
});
export type ExtractedPage = z.infer<typeof ExtractedPageSchema>;

export const ExtractedResponseSchema = z.object({
  pages: z.array(ExtractedPageSchema).default([]),
});

/**
 * One machine claim awaiting review — the CANDIDATE tier.
 *
 * Not an IntelligenceEntity. A row here is something a model asserted about a
 * document and no person has agreed with; promoting it is a separate, human act
 * because 28 CFR 23.20(a) forbids entering a person into a criminal intelligence
 * system without a predicate only a person can state.
 */
export const EntityCandidateSchema = z.object({
  id: z.string(),
  caseId: z.string(),
  evidenceId: z.string(),
  jobId: z.string(),
  pageNumber: z.number().int(),
  chunkId: z.string().nullable(),
  kind: z.string(),
  value: z.string(),
  normalizedValue: z.string().nullable(),
  /** The quoted support the reviewer checks against the page. */
  verbatimSpan: z.string().nullable(),
  confidenceScore: z.number().nullable(),
  /** Provenance, so an old claim can be attributed when a prompt turns out wrong. */
  modelId: z.string().nullable(),
  promptVersion: z.string().nullable(),
});
export type EntityCandidate = z.infer<typeof EntityCandidateSchema>;

export const EntityCandidatesResponseSchema = z.object({
  candidates: z.array(EntityCandidateSchema).default([]),
});

/** What a reviewer may decide — the vocabulary migration 0018's CHECK permits. */
export const TriageDecisionSchema = z.enum(["accepted", "rejected", "duplicate"]);
export type TriageDecisionValue = z.infer<typeof TriageDecisionSchema>;

export const TriageResponseSchema = z.object({ status: z.string() });

export const IrisChatResponseSchema = z.object({
  reply: z.string(),
  modelId: z.string(),
  usage: z.object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
  }),
});
export type IrisChatResponse = z.infer<typeof IrisChatResponseSchema>;

export const ExtractionJobSchema = z.object({
  id: z.string(),
  evidenceId: z.string(),
  status: z.string(),
  source: z.string(),
  modelId: z.string().nullable().optional(),
  pageCount: z.number().int().nullable().optional(),
}).passthrough();
export const ExtractJobResponseSchema = z.object({ job: ExtractionJobSchema });
export type ExtractionJob = z.infer<typeof ExtractionJobSchema>;

// -- Capability 3: Entity resolution + link analysis -----------------------
// Wire shape mirrors apps/app entities-ontology.ts (IntelligenceEntity +
// EntityRelationship) so the hooks can hand results straight to the workspace.

export const EntityAttributeSchema = z.object({
  name: z.string(),
  value: z.string(),
  sourceCitation: z.string(),
});

/**
 * One branch off an object, for the links panel.
 *
 * Mirrors `WireLinkBranch` in @repo/ontology-sdk — the API returns
 * `{ branches: [...] }` from `/api/ontology/objects/:type/:id/links`, and the
 * port already omits branches with nothing on the end of them.
 *
 * `label` is pre-worded for its direction ("has evidence" vs "belongs to case"),
 * so the panel renders it verbatim rather than deriving wording from the link
 * type and a direction flag — two places to get the grammar wrong.
 */
export const LinkBranchSchema = z.object({
  linkTypeId: z.string(),
  direction: z.enum(["forward", "reverse"]),
  label: z.string(),
  otherType: z.string(),
  count: z.number(),
});

export const LinkBranchesResponseSchema = z.object({
  branches: z.array(LinkBranchSchema).default([]),
});

export const IntelligenceEntitySchema = z.object({
  id: z.string(),
  // NER-02 added drug, license_plate and money server-side; the client must
  // accept them or zod rejects a valid response.
  kind: z.enum([
    "person",
    "phone",
    "vehicle",
    "organization",
    "location",
    "account",
    "drug",
    "license_plate",
    "money",
  ]),
  canonicalIdentifier: z.string(),
  displayName: z.string(),
  originCaseReference: z.string(),
  attributes: z.array(EntityAttributeSchema).default([]),
  registeredAt: z.string(),
  lastReviewedAt: z.string(),
  // HITL-06. Defaulted rather than required so a response from an older API
  // still parses — and the default is the untrusted value, matching the server.
  reviewStatus: z.enum(["suggested", "confirmed", "rejected"]).optional(),
  reviewedBy: z.string().nullable().optional(),
  reviewedAt: z.string().nullable().optional(),
  // NER-06: set when this record has been fused into another.
  canonicalEntityId: z.string().nullable().optional(),
});

export const EntityRelationshipSchema = z.object({
  id: z.string(),
  sourceEntityId: z.string(),
  targetEntityId: z.string(),
  relationshipKind: z.enum([
    "associate_of",
    "communicated_with",
    "located_at",
    "owns",
    "member_of",
    "linked_to",
  ]),
  assertionConfidence: z.enum(["confirmed", "probable", "possible"]),
  sourceReferenceCitation: z.string(),
  assertedBy: z.string(),
  assertedAt: z.string(),
});

export const EntitiesResponseSchema = z.object({
  entities: z.array(IntelligenceEntitySchema).default([]),
});
export const EntityGraphResponseSchema = z.object({
  entities: z.array(IntelligenceEntitySchema).default([]),
  relationships: z.array(EntityRelationshipSchema).default([]),
});

// -- Entity review: provenance, resolution candidates, fusion (NER-05/06, HITL) --

/** One observation of an entity in one source (NER-08). */
export const EntityProvenanceSchema = z.object({
  id: z.string(),
  evidenceId: z.string().nullable().default(null),
  extractionJobId: z.string().nullable().default(null),
  pageNumber: z.number().nullable().default(null),
  /** The literal span as read — what the analyst is shown, not a paraphrase. */
  observedValue: z.string(),
  /** Which model or deterministic rule produced it (HITL-05). */
  method: z.string(),
  modelId: z.string().nullable().default(null),
  promptVersion: z.string().nullable().default(null),
  confidence: z.number().nullable().default(null),
  observedAt: z.string(),
});
export const EntityProvenanceResponseSchema = z.object({
  provenance: z.array(EntityProvenanceSchema).default([]),
});
export type EntityProvenance = z.infer<typeof EntityProvenanceSchema>;

/** A scored duplicate candidate. `breakdown` is why — a reviewer disagrees with reasoning, not just verdicts. */
export const ResolutionCandidateSchema = z.object({
  entityId: z.string(),
  candidateId: z.string(),
  displayName: z.string(),
  candidateName: z.string(),
  score: z.object({
    score: z.number(),
    breakdown: z.array(z.string()).default([]),
    blocked: z.boolean(),
  }),
  proposeFusion: z.boolean(),
});
export const ResolutionCandidatesResponseSchema = z.object({
  candidates: z.array(ResolutionCandidateSchema).default([]),
});
export type ResolutionCandidate = z.infer<typeof ResolutionCandidateSchema>;

/** What fusion would produce, before committing to it. */
export const FusionPreviewSchema = z.object({
  canonical: z.object({ id: z.string(), displayName: z.string(), kind: z.string() }),
  duplicate: z.object({ id: z.string(), displayName: z.string(), kind: z.string() }),
  conflicting: z
    .array(z.object({ name: z.string(), canonicalValue: z.string(), duplicateValue: z.string() }))
    .default([]),
  agreeing: z.array(z.object({ name: z.string(), value: z.string() })).default([]),
  contributed: z.array(z.object({ name: z.string(), value: z.string() })).default([]),
  reversible: z.boolean(),
});
export type FusionPreview = z.infer<typeof FusionPreviewSchema>;

// -- CaseOverview: case activity timeline ----------------------------------

export const CaseTimelineEventSchema = z.object({
  id: z.string(),
  caseId: z.string(),
  occurredAt: z.string(),
  summary: z.string(),
  source: z.enum(["manual", "ingest", "ai", "system"]),
  confidence: z.number().optional(),
  // -- CAP 3 Case Overview (main) 10-column event log (all optional) -------
  /** "Report #". */
  reportNumber: z.string().optional(),
  /** "Officer". */
  officer: z.string().optional(),
  /** "Actions" — plain-language action text. */
  actions: z.string().optional(),
  /** "Persons" — joined person ids. */
  personIds: z.array(z.string()).default([]),
  /** "Evidence" — joined evidence ids. */
  evidenceIds: z.array(z.string()).default([]),
  /** "Warrants" — joined warrant ids. */
  warrantIds: z.array(z.string()).default([]),
  /** "BWC" — MANUAL, blank default; never auto, never triggers a flag. */
  bwc: z.string().optional(),
  /** "Investigator Flags" — detective-authored free text. */
  investigatorFlags: z.string().optional(),
  /** "Follow-up Needed". */
  followUpNeeded: z.string().optional(),
  /** "Discovery Status" — MANUAL, blank default; never auto, never flags. */
  discoveryStatus: z.string().optional(),
  /** Field names IronCase auto-populated (for the auto/manual export fill). */
  autoFields: z.array(z.string()).default([]),
});
export type CaseTimelineEvent = z.infer<typeof CaseTimelineEventSchema>;

export const TimelineResponseSchema = z.object({
  timeline: z.array(CaseTimelineEventSchema).default([]),
});

// -- CAP 4: Gap-detection flags --------------------------------------------
// detectGaps() (lib/gap-detection.ts) runs client-side over the joined case
// query data and emits Gap[]. Categories: A Missing Actions, B Missing
// Documentation, C Time-Sensitive. Stable, deterministic ids let dismissals
// persist in localStorage.

export const GapCategory = z.enum(["A", "B", "C"]);
export type GapCategory = z.infer<typeof GapCategory>;

export const GapSeverity = z.enum(["urgent", "high", "medium", "low"]);
export type GapSeverity = z.infer<typeof GapSeverity>;

export const GapSchema = z.object({
  /** Stable, deterministic (rule + subject id) so dismissals persist. */
  id: z.string(),
  category: GapCategory,
  /** Rule id, e.g. "A-a-uninterviewed-witness". */
  ruleId: z.string(),
  /** "What was flagged". */
  title: z.string(),
  /** "Why". */
  why: z.string(),
  /** Which source document triggered it. */
  sourceRef: SourceRefSchema.optional(),
  /** "Suggested next action". */
  suggestedAction: z.string(),
  severity: GapSeverity,
  /** ISO deadline for the Category-C countdown. */
  countdownTo: z.string().optional(),
});
export type Gap = z.infer<typeof GapSchema>;

// -- CAP 5: Structured warrant draft ---------------------------------------
// A STRUCTURED draft populated from extracted case facts — NOT a free-form AI
// PC narrative. The probable-cause narrative is a detective-owned free-text
// field. IronCase never submits anywhere.

export const WarrantDraftSchema = z.object({
  type: WarrantType,
  parties: z.array(z.string()).default([]),
  propertyOrAccounts: z.array(z.string()).default([]),
  itemsToSeize: z.array(z.string()).default([]),
  /** Bullet facts drawn from extracted data. */
  supportingFacts: z.array(z.string()).default([]),
  /** DETECTIVE-OWNED free text — IronCase never authors this. */
  probableCauseNarrative: z.string().default(""),
  serviceProvider: z.string().optional(),
});
export type WarrantDraft = z.infer<typeof WarrantDraftSchema>;

// -- Endpoints --------------------------------------------------------------

export const api = {
  health: () => request("/api/health", HealthSchema),
  me: () => request("/api/me", MeSchema),
  listCases: () => request("/api/cases", CasesResponseSchema),
  listEvidence: (caseId: string) =>
    request(`/api/cases/${encodeURIComponent(caseId)}/evidence`, EvidenceResponseSchema),
  listPersons: (caseId: string) =>
    request(`/api/cases/${encodeURIComponent(caseId)}/persons`, PersonsResponseSchema),
  listWarrants: (caseId: string) =>
    request(`/api/cases/${encodeURIComponent(caseId)}/warrants`, WarrantsResponseSchema),
  listSurveillance: (caseId: string) =>
    request(`/api/cases/${encodeURIComponent(caseId)}/surveillance`, SurveillanceResponseSchema),

  /** Per-user favorites (server-backed; localStorage is just an optimistic cache). */
  listFavorites: () => request("/api/favorites", FavoritesResponseSchema),
  /** Links off one object, grouped for display by groupLinkBranches(). */
  linkBranches: (type: string, id: string) =>
    request(
      `/api/ontology/objects/${encodeURIComponent(type)}/${encodeURIComponent(id)}/links`,
      LinkBranchesResponseSchema,
    ),
  addFavorite: (resourceType: string, resourceId: string) =>
    request(
      `/api/favorites/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceId)}`,
      FavoriteOkSchema,
      { method: "PUT" },
    ),
  removeFavorite: (resourceType: string, resourceId: string) =>
    request(
      `/api/favorites/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceId)}`,
      FavoriteOkSchema,
      { method: "DELETE" },
    ),

  /** Link-analysis graph (single-case MVP → whole graph). */
  listEntities: () => request("/api/entities", EntitiesResponseSchema),
  getEntityGraph: () => request("/api/entity-graph", EntityGraphResponseSchema),

  /**
   * P6 Human-in-the-loop approvals (HumanDecisioning). The queue + detail are
   * agency-scoped reads (RLS-walled); `decide` enforces the author≠approver SoD
   * (→ 403), mandatory reviewer notes (→ 400), and the idempotent double-decide
   * guard (→ 409 on an already-decided approval). agencyId is resolved
   * server-side from the session — never sent by the client.
   */
  entityReview: {
    /** GET → every source that mentioned this entity, newest first (HITL-05). */
    provenance: (id: string) =>
      request(
        `/api/entities/${encodeURIComponent(id)}/provenance`,
        EntityProvenanceResponseSchema
      ),

    /** GET → scored duplicate candidates, with the reasoning (NER-05). */
    candidates: (id: string) =>
      request(
        `/api/entities/${encodeURIComponent(id)}/candidates`,
        ResolutionCandidatesResponseSchema
      ),

    /** GET → what fusion WOULD do. Never mutates. */
    fusionPreview: (id: string, canonicalId: string) =>
      request(
        `/api/entities/${encodeURIComponent(id)}/fusion-preview?canonicalId=${encodeURIComponent(canonicalId)}`,
        FusionPreviewSchema
      ),

    /** POST → confirm or reject. A reason is required even to confirm (HITL-07). */
    review: (id: string, body: { status: "confirmed" | "rejected"; reason: string }) =>
      request(`/api/entities/${encodeURIComponent(id)}/review`, z.object({ status: z.string() }), {
        method: "POST",
        body: JSON.stringify(body),
      }),

    /** Warrant drafting and the detective → supervisor approval chain. */
    warrants: {
      create: (
        caseId: string,
        body: {
          warrantNumber: string;
          type: string;
          target: string;
          court?: string;
          calecpaPredicate?: string;
        }
      ) =>
        request(
          `/api/cases/${encodeURIComponent(caseId)}/warrants`,
          z.object({ warrant: WarrantSchema }),
          { method: "POST", body: JSON.stringify(body) }
        ),

      /** POST → machine-draft the probable-cause narrative onto a DRAFT warrant. */
      draftAffidavit: (id: string) =>
        request(
          `/api/warrants/${encodeURIComponent(id)}/affidavit`,
          z.object({ affidavit: z.string(), modelId: z.string() }),
          { method: "POST" }
        ),

      /** POST → draft becomes pending_hitl. The detective puts their name to it. */
      submit: (id: string) =>
        request(
          `/api/warrants/${encodeURIComponent(id)}/submit`,
          z.object({ status: z.string() }),
          { method: "POST" }
        ),

      /** POST → sign or reject. Refused if the decider submitted it. */
      decide: (id: string, body: { status: "signed" | "rejected"; reason: string }) =>
        request(
          `/api/warrants/${encodeURIComponent(id)}/decision`,
          z.object({ status: z.string() }),
          { method: "POST", body: JSON.stringify(body) }
        ),

      /** GET → the supervisor queue. */
      pending: () => request("/api/warrants/pending", WarrantsResponseSchema),
    },

    /**
     * POST → decide many at once (HITL-02).
     *
     * Reports `failed` per id rather than throwing: entities that went stale
     * mid-review must not discard the decisions that did land.
     */
    reviewBatch: (body: {
      entityIds: string[];
      status: "confirmed" | "rejected";
      reason: string;
    }) =>
      request(
        "/api/entities/review-batch",
        z.object({
          decided: z.number(),
          failed: z.array(z.object({ entityId: z.string(), error: z.string() })),
        }),
        { method: "POST", body: JSON.stringify(body) }
      ),

    /** POST → point this entity at a canonical one. Reversible (NER-06). */
    fuse: (id: string, body: { canonicalId: string; reason: string }) =>
      request(
        `/api/entities/${encodeURIComponent(id)}/fuse`,
        z.object({ duplicateId: z.string(), canonicalId: z.string() }),
        { method: "POST", body: JSON.stringify(body) }
      ),

    /** POST → undo a fusion. */
    unfuse: (id: string, body: { reason: string }) =>
      request(`/api/entities/${encodeURIComponent(id)}/unfuse`, z.object({ unfused: z.boolean() }), {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },

  approvals: {
    /** GET /api/v1/approvals[?status=&riskLevel=] → the agency's queue. */
    list: (filter?: ApprovalListFilter) => {
      const qs = new URLSearchParams();
      if (filter?.status) qs.set("status", filter.status);
      if (filter?.riskLevel) qs.set("riskLevel", filter.riskLevel);
      const q = qs.toString();
      return request(`/api/v1/approvals${q ? `?${q}` : ""}`, ApprovalsResponseSchema);
    },

    /** GET /api/v1/approvals/:id → a single approval (404 if out of scope). */
    get: (id: string) =>
      request(`/api/v1/approvals/${encodeURIComponent(id)}`, ApprovalResponseSchema),

    /** POST /api/v1/approvals/:id/decide → transition pending→approved|rejected. */
    decide: (id: string, body: { decision: ApprovalDecision; reviewerNotes: string }) =>
      request(`/api/v1/approvals/${encodeURIComponent(id)}/decide`, ApprovalResponseSchema, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
  },

  /** Identity & Access ("Multipass") settings reference lists. */
  listRoleSets: () => request("/api/access/role-sets", RoleSetsResponseSchema),
  listOrganizations: () => request("/api/access/organizations", OrganizationsResponseSchema),
  listMarkings: () => request("/api/access/markings", MarkingsResponseSchema),
  listAuthorizedApplications: () =>
    request("/api/access/authorized-applications", AuthorizedApplicationsResponseSchema),

  /** Case activity feed (Overview-tab timeline). */
  listCaseTimeline: (caseId: string) =>
    request(`/api/cases/${encodeURIComponent(caseId)}/timeline`, TimelineResponseSchema),

  /** Reserve an evidence row + presigned S3 PUT URL for direct upload. */
  requestUploadUrl: (caseId: string, body: { title: string; contentType: string; sizeBytes: number }) =>
    request(
      `/api/cases/${encodeURIComponent(caseId)}/evidence/upload-url`,
      UploadUrlResponseSchema,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
    ),

  /**
   * PUT the file bytes directly to the presigned S3 URL. Browser → S3 — does
   * NOT go through the API Lambda (bypasses the 6MB sync payload limit).
   * The Content-Type header MUST match what we passed when reserving the URL
   * or S3 returns 403 SignatureDoesNotMatch.
   */
  uploadToS3: async (url: string, file: File): Promise<void> => {
    const res = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    });
    if (!res.ok) {
      throw new ApiError(res.status, `S3 upload failed: ${res.status} ${res.statusText}`);
    }
  },

  /** Kick off an extraction job for an existing evidence row. */
  runExtraction: (evidenceId: string, body: { key: string; contentType: string; sizeBytes: number }) =>
    request(
      `/api/evidence/${encodeURIComponent(evidenceId)}/extract`,
      ExtractJobResponseSchema,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
    ),

  /** Latest succeeded extraction's pages, in page order. */
  getExtractedPages: (evidenceId: string) =>
    request(`/api/evidence/${encodeURIComponent(evidenceId)}/extracted`, ExtractedResponseSchema),

  /** The case's untriaged machine claims, newest first. */
  getEntityCandidates: (caseId: string, limit = 200) =>
    request(
      `/api/cases/${encodeURIComponent(caseId)}/entity-candidates?limit=${limit}`,
      EntityCandidatesResponseSchema,
    ),

  /**
   * Record a decision about one claim.
   *
   * Case-scoped in the PATH because the write is conditioned on it server-side:
   * `case.write` is authorized per case, so a candidate id alone would let a
   * reviewer assigned to one case decide a claim on another.
   */
  triageEntityCandidate: (
    caseId: string,
    candidateId: string,
    body: { decision: TriageDecisionValue; note: string },
  ) =>
    request(
      `/api/cases/${encodeURIComponent(caseId)}/entity-candidates/${encodeURIComponent(candidateId)}/triage`,
      TriageResponseSchema,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
    ),

  /**
   * The agency's audit trail, newest first.
   *
   * Hash-chained server-side (`prevHash`/`rowHash` on every row), which is why
   * the reader shows the outcome and status of each entry rather than a
   * prettified summary: the value of this table is that it is verbatim.
   */
  listAudit: (limit = 100, cursor?: string) =>
    request(
      `/api/audit?limit=${limit}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
      AuditResponseSchema,
    ),

  /**
   * Federated identity providers configured for this deployment.
   *
   * Returns 503 with `{ providers: [], error: "auth not configured" }` when no
   * identity system is wired, which the page reports as a state rather than a
   * failure — an unconfigured deployment is a normal thing to be looking at.
   */
  listIdentityProviders: () =>
    request(`/api/admin/identity-providers`, IdentityProvidersResponseSchema),

  /**
   * Register a federated identity provider.
   *
   * The body shape is the server's `parseCreateProviderBody`: `name`, `type`
   * ("SAML" | "OIDC"), an `attributeMapping`, and a `saml` or `oidc` block
   * depending on the type. Modelled rather than loosely typed, because a
   * malformed provider is rejected with a specific message and a caller that
   * cannot construct the body cannot read that message.
   *
   * A CLIENT SECRET GOES IN AND NEVER COMES BACK: the list endpoint returns
   * name/type/enabled only. Nothing here should ever put one on screen.
   */
  createIdentityProvider: (body: IdentityProviderInput) =>
    request(`/api/admin/identity-providers`, IdentityProviderWriteSchema, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  deleteIdentityProvider: (name: string) =>
    request(`/api/admin/identity-providers/${encodeURIComponent(name)}`, IdentityProviderWriteSchema, {
      method: "DELETE",
    }),

  /** IRIS AI chat — Bedrock-backed Claude messages-API call. */
  chatIris: (body: {
    messages: { role: "user" | "assistant"; content: string }[];
    caseId?: string;
    caseTitle?: string;
  }) =>
    request(`/api/iris/chat`, IrisChatResponseSchema, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  /**
   * RBAC admin — Roles & Cedar policies for the Admin Settings UI.
   *
   * All methods require a platform_admin Bearer token; agencyId is resolved
   * server-side from the session. Returns 503 "policy engine not configured"
   * until CEDAR_POLICY_STORES is set (ADR-0022 wiring). See 12-01-SUMMARY.md.
   */
  rbacAdmin: {
    /** GET /api/admin/rbac/roles → the agency's roles. */
    listRoles: () => request("/api/admin/rbac/roles", RolesResponseSchema),

    /** GET /api/admin/rbac/roles/:roleId → a single role. */
    getRole: (roleId: string) =>
      request(`/api/admin/rbac/roles/${encodeURIComponent(roleId)}`, RoleResponseSchema),

    /** POST /api/admin/rbac/roles → create a role (201). */
    createRole: (body: RoleInput) =>
      request("/api/admin/rbac/roles", RoleResponseSchema, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),

    /** PUT /api/admin/rbac/roles/:roleId → replace name/description/permissions. */
    updateRole: (roleId: string, body: RoleInput) =>
      request(`/api/admin/rbac/roles/${encodeURIComponent(roleId)}`, RoleResponseSchema, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),

    /** DELETE /api/admin/rbac/roles/:roleId → { status, roleId }. */
    deleteRole: (roleId: string) =>
      request(`/api/admin/rbac/roles/${encodeURIComponent(roleId)}`, RoleDeletedResponseSchema, {
        method: "DELETE",
      }),

    /** GET /api/admin/rbac/roles/:roleId/policy → raw Cedar policy text. */
    getRolePolicy: (roleId: string) =>
      request(`/api/admin/rbac/roles/${encodeURIComponent(roleId)}/policy`, RolePolicyResponseSchema),

    /** PUT /api/admin/rbac/roles/:roleId/policy → save edited Cedar text. */
    updateRolePolicy: (roleId: string, cedarText: string) =>
      request(`/api/admin/rbac/roles/${encodeURIComponent(roleId)}/policy`, RolePolicyResponseSchema, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cedarText }),
      }),

    // -- Principal assignments (ADMIN-03/05/06) ----------------------------
    // Assign / list / remove the users & groups granted a role. Each mutation
    // is high-sensitivity and audit-logged server-side (fail-closed) per
    // 12-01, so there is no client-side audit here.

    /** GET /api/admin/rbac/roles/:roleId/principals → assigned users + groups. */
    listRolePrincipals: (roleId: string) =>
      request(
        `/api/admin/rbac/roles/${encodeURIComponent(roleId)}/principals`,
        PrincipalsResponseSchema,
      ),

    /** POST /api/admin/rbac/roles/:roleId/principals → grant a principal (201). */
    assignPrincipal: (roleId: string, principal: PrincipalRef) =>
      request(
        `/api/admin/rbac/roles/${encodeURIComponent(roleId)}/principals`,
        PrincipalAssignedResponseSchema,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(principal),
        },
      ),

    /**
     * DELETE /api/admin/rbac/roles/:roleId/principals/:principalId?type=…
     * Removes a principal from a role. The principalType rides as the `?type=`
     * query param (DELETE carries no body) — it disambiguates user vs group.
     */
    unassignPrincipal: (roleId: string, principal: PrincipalRef) =>
      request(
        `/api/admin/rbac/roles/${encodeURIComponent(roleId)}/principals/${encodeURIComponent(principal.principalId)}?type=${principal.principalType}`,
        PrincipalAssignedResponseSchema,
        { method: "DELETE" },
      ),

    /**
     * Assignable principals — the directory of users + groups an admin can
     * grant to a role. Sourced from the Cognito-backed
     * GET /api/admin/rbac/principals endpoint added in 12-04 (no longer a stub
     * that re-reads the role's own principals). The picker filters out
     * principals already assigned to the role client-side.
     *
     * `kind` narrows the directory to one type (rides as `?type=user|group`);
     * omit it to return both. The response reuses PrincipalsResponseSchema —
     * the backend maps each directory entry's label to `displayName` so a
     * candidate row renders identically to an assigned row.
     *
     * Fail-closed: when the API role lacks cognito-idp:ListUsers/ListGroups (or
     * the pool is unset), this returns 503 — the panel surfaces a Callout and
     * keeps the manual add-by-id fallback so the screen still works. The
     * `roleId` is accepted for call-site symmetry with the other principal
     * methods but is not part of the directory path (the directory is
     * agency-wide, not role-scoped).
     */
    listAssignablePrincipals: (_roleId: string, kind?: PrincipalType) =>
      request(
        `/api/admin/rbac/principals${kind ? `?type=${kind}` : ""}`,
        PrincipalsResponseSchema,
      ),
  },
};

// `baseUrl` is no longer exported: the origin now lives on the HttpClientPort
// (`container().http.baseUrl`) so there is a single source of truth. Nothing
// imported the old export.
export { ApiError };
